import { last, isEmpty, isNil } from 'lodash';
import { CouponItem } from '../../types/billing';
import { DEFAULT_TRIAL_DURATION_DAYS, TRIAL_TYPE_COUPONS } from '../../utils/billing';
import { SharedBillingState } from '../defaults/shared-billing';

export function getSubscriptionCodeValid({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.subscriptionCodeValid;
}

export function getSubscriptionCode({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.subscriptionCode;
}

export function getSubscriptionCodeDetails({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  return sharedBilling.subscriptionCodeDetails;
}

export function getMainSubscriptionCodeItem({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  const subscriptionCodeDetails = getSubscriptionCodeDetails({ sharedBilling });
  return last(subscriptionCodeDetails) || ({} as CouponItem);
}

export function getSubscriptionCodeValidationError({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  return sharedBilling.subscriptionCodeValidationError;
}

export function getSubscriptionCodeType({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.subscriptionCodeType;
}

export function getSubscriptionCodeTermsUrl({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  return sharedBilling.subscriptionCodeTermsUrl;
}

export function getIsCheckingSubscriptionCode({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  return sharedBilling.isCheckingSubscriptionCode;
}

export function getShowTMODisclaimer({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  const subscriptionCode = getSubscriptionCode({ sharedBilling });

  return !!subscriptionCode.match(/^P?(VIB|TMO)/i);
}

export function getIsExtendedTrialOffer({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  const { benefitType } = getMainSubscriptionCodeItem({ sharedBilling });

  return TRIAL_TYPE_COUPONS.has(benefitType);
}

export function getTrialDays({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  let trialDays;
  const subscriptionCodeItem = getMainSubscriptionCodeItem({ sharedBilling });
  const isExtendedTrialOffer = getIsExtendedTrialOffer({ sharedBilling });

  if (isExtendedTrialOffer) {
    const { benefitDurationMonths: trialMonths, benefitAmount } = subscriptionCodeItem;
    trialDays = !isNil(trialMonths) && trialMonths > 0 ? trialMonths * 30 : benefitAmount;
  }

  return trialDays || DEFAULT_TRIAL_DURATION_DAYS;
}

export function getStripePaymentErrorMessage({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  return sharedBilling.stripePaymentErrorMessage;
}

export function getStripePaymentLoading({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.stripePaymentLoading;
}

export function getStripePaymentReady({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.stripePaymentReady;
}

export function getStripePaymentMethodId({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.stripePaymentMethodId;
}

export function getSetupIntentId({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.setupIntentId;
}

export function getZipCode({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.zipCode;
}

export function getSelectedPackageId({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.selectedPackageId;
}

export function getSelectedAddOnIds({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.selectedAddOnIds;
}

export function getShouldSetupIntent({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.shouldSetupIntent;
}

export function getSkipCustomZipCollection({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  return sharedBilling.skipCustomZipCollection;
}

export function getCanSubmitStripePayment({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  const { stripePaymentReady, stripePaymentLoading, zipCode, skipCustomZipCollection } =
    sharedBilling;

  const zipRequirementFilled = skipCustomZipCollection || !isEmpty(zipCode);
  return stripePaymentReady && !stripePaymentLoading && zipRequirementFilled;
}

export function getPriceChangeAnnouncementDate({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  return sharedBilling.priceChangeAnnouncementDate;
}

export function getPriceChangePackageId({ sharedBilling }: { sharedBilling: SharedBillingState }) {
  return sharedBilling.priceChangePackageId;
}

export function getPriceChangeIncreasedPriceCents({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  return sharedBilling.priceChangeIncreasedPriceCents;
}

export function getPriceChangeEffectiveDate({
  sharedBilling,
}: {
  sharedBilling: SharedBillingState;
}) {
  return sharedBilling.priceChangeEffectiveDate;
}
